import { Injectable } from '@angular/core';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { Action, State, StateContext } from '@ngxs/store';
import { GetMyGroupsAction } from './my-groups.actions';
import { GroupDetailsDto, GroupsService } from '@smatchy/sdk';
import { catchError, map, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { ShowToastErrorAction } from '../../app';
import { TranslateService } from '@ngx-translate/core';

export interface MyGroupsStateModel {
  groups: GroupDetailsDto[];
  loading: boolean;
}

const defaults: MyGroupsStateModel = {
  groups: [],
  loading: false,
};

@State<MyGroupsStateModel>({
  name: 'myGroups',
  defaults,
})
@Injectable()
export class MyGroupsState {
  constructor(
    private readonly _groupsService: GroupsService,
    private readonly _store: Store,
    private readonly _translate: TranslateService,
  ) {}

  @Action(GetMyGroupsAction)
  getMyGroupsAction({ getState, patchState }: StateContext<MyGroupsStateModel>) {
    if (getState().loading) {
      return;
    }

    patchState({
      loading: true,
    });
    return this._groupsService.getMyGroups().pipe(
      map((res) => {
        patchState({ loading: false });

        if (!carriesAValue(res)) {
          this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.ERROR')));
          return res;
        }

        patchState({ groups: res.value.groups ?? [] });
        return res;
      }),
      catchError((error: unknown) => {
        // The call threw before any response, so the map above never ran: reset
        // the spinner it would otherwise leave turning forever.
        patchState({ loading: false });
        // Status 0 means the request never completed — offline, or the app went
        // to the background mid-request (CAPACITOR-1). That is a transport
        // hiccup, not a defect: it must not surface as an unhandled error to
        // Sentry, and a toast would blame the person for losing signal. Any
        // other transport error still tells them something went wrong.
        const isNetworkDrop = error instanceof HttpErrorResponse && error.status === 0;
        if (!isNetworkDrop) {
          this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GENERICS.ERROR')));
        }
        return of(null);
      }),
    );
  }
}
